import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  TextField,
  Button,
  Alert,
  Divider,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  CircularProgress,
  InputAdornment,
  Grid,
  Paper,
  Chip,
} from '@mui/material';
import {
  AttachMoney as MoneyIcon,
  Save as SaveIcon,
  RestartAlt as ResetIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Star as StarIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import {
  getPaymentSettings,
  updatePaymentSettings,
  validatePaymentSettings,
  calculateDeposit,
  DEFAULT_PAYMENT_SETTINGS,
  type PaymentSettings,
} from '@/services/settingsService';
import { RegionalVATConfig } from '@/components/RegionalVATConfig';
import { RegionalDeliveryFeeConfig } from '@/components/RegionalDeliveryFeeConfig';

const PaymentSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<PaymentSettings>(DEFAULT_PAYMENT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const result = await getPaymentSettings();
      if (result.success && result.data) {
        setSettings(result.data);
      } else {
        setSettings(DEFAULT_PAYMENT_SETTINGS);
      }
    } catch (error) {
      console.error('Error loading payment settings:', error);
      toast.error('Failed to load payment settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    const validationErrors = validatePaymentSettings(settings);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error('Please fix the errors before saving');
      return;
    }

    setErrors({});
    setSaving(true);

    try {
      const result = await updatePaymentSettings(settings);
      if (result.success) {
        toast.success('Payment settings saved successfully');
      } else {
        toast.error(result.error || 'Failed to save payment settings');
      }
    } catch (error) {
      console.error('Error saving payment settings:', error);
      toast.error('Failed to save payment settings');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setSettings(DEFAULT_PAYMENT_SETTINGS);
    setErrors({});
    toast.success('Reset to default settings');
  };

  const clearError = (field: string) => {
    if (errors[field]) {
      const newErrors = { ...errors };
      delete newErrors[field];
      setErrors(newErrors);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Page Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <MoneyIcon />
          Payment Settings
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure fees, discounts, and security deposits
        </Typography>
      </Box>

      {/* Booking Fee Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Booking Fee (Visible to Renters)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Fee charged to renters and added to their total payment
          </Typography>

          <Box sx={{ mb: 3 }}>
            <FormControl component="fieldset">
              <FormLabel component="legend">
                <Typography variant="body1" fontWeight="medium" color="text.primary">
                  Fee Type
                </Typography>
              </FormLabel>
              <RadioGroup
                value={settings.bookingFee.type}
                onChange={(e) =>
                  setSettings({
                    ...settings,
                    bookingFee: { ...settings.bookingFee, type: e.target.value as 'percentage' | 'flat' },
                  })
                }
                sx={{ mt: 1 }}
              >
                <FormControlLabel
                  value="percentage"
                  control={<Radio />}
                  label="Percentage of Rental Cost"
                />
                <FormControlLabel value="flat" control={<Radio />} label="Fixed Amount" />
              </RadioGroup>
            </FormControl>
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                Percentage
              </Typography>
              <TextField
                type="number"
                value={settings.bookingFee.percentage}
                onChange={(e) => {
                  setSettings({
                    ...settings,
                    bookingFee: { ...settings.bookingFee, percentage: Number(e.target.value) },
                  });
                  clearError('bookingFeePercentage');
                }}
                inputProps={{ min: 0, max: 100 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                error={!!errors.bookingFeePercentage}
                helperText={errors.bookingFeePercentage || 'Used when type is "percentage"'}
                fullWidth
                disabled={settings.bookingFee.type !== 'percentage'}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                Flat Amount
              </Typography>
              <TextField
                type="number"
                value={settings.bookingFee.flatAmount}
                onChange={(e) => {
                  setSettings({
                    ...settings,
                    bookingFee: { ...settings.bookingFee, flatAmount: Number(e.target.value) },
                  });
                  clearError('bookingFeeFlatAmount');
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                }}
                error={!!errors.bookingFeeFlatAmount}
                helperText={errors.bookingFeeFlatAmount || 'Used when type is "flat"'}
                fullWidth
                disabled={settings.bookingFee.type !== 'flat'}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Service Charges Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Service Charges (Hidden from Renters)
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Platform commission deducted from host/driver payouts
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                Fleet Owner Charge
              </Typography>
              <TextField
                type="number"
                value={settings.serviceFees.fleetOwner}
                onChange={(e) => {
                  setSettings({
                    ...settings,
                    serviceFees: { ...settings.serviceFees, fleetOwner: Number(e.target.value) },
                  });
                  clearError('serviceFeeFleetOwner');
                }}
                inputProps={{ min: 0, max: 100 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                error={!!errors.serviceFeeFleetOwner}
                helperText={errors.serviceFeeFleetOwner || 'Default: 10%'}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                Individual Host Charge
              </Typography>
              <TextField
                type="number"
                value={settings.serviceFees.individualHost}
                onChange={(e) => {
                  setSettings({
                    ...settings,
                    serviceFees: { ...settings.serviceFees, individualHost: Number(e.target.value) },
                  });
                  clearError('serviceFeeIndividualHost');
                }}
                inputProps={{ min: 0, max: 100 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                error={!!errors.serviceFeeIndividualHost}
                helperText={errors.serviceFeeIndividualHost || 'Default: 15%'}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography variant="body2" fontWeight="medium" gutterBottom>
                Driver Charge
              </Typography>
              <TextField
                type="number"
                value={settings.serviceFees.driver}
                onChange={(e) => {
                  setSettings({
                    ...settings,
                    serviceFees: { ...settings.serviceFees, driver: Number(e.target.value) },
                  });
                  clearError('serviceFeeDriver');
                }}
                inputProps={{ min: 0, max: 100 }}
                InputProps={{
                  endAdornment: <InputAdornment position="end">%</InputAdornment>,
                }}
                error={!!errors.serviceFeeDriver}
                helperText={errors.serviceFeeDriver || 'Default: 20%'}
                fullWidth
              />
            </Grid>
          </Grid>

          <Alert severity="info" sx={{ mt: 2 }}>
            Service charges are deducted from host/driver earnings at payout and are NOT shown to renters
            during booking.
          </Alert>
        </CardContent>
      </Card>

      {/* First-Time User Discount Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            First-Time User Discount
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Automatic discount for users making their first booking
          </Typography>

          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.firstTimeUserDiscount.enabled}
                  onChange={(e) =>
                    setSettings({
                      ...settings,
                      firstTimeUserDiscount: {
                        ...settings.firstTimeUserDiscount,
                        enabled: e.target.checked,
                      },
                    })
                  }
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Enable First-Time User Discount
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Applies automatically when user has 0 completed bookings
                  </Typography>
                </Box>
              }
            />
          </Box>

          {settings.firstTimeUserDiscount.enabled && (
            <>
              <Box sx={{ mb: 3 }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    <Typography variant="body1" fontWeight="medium" color="text.primary">
                      Discount Type
                    </Typography>
                  </FormLabel>
                  <RadioGroup
                    value={settings.firstTimeUserDiscount.type}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        firstTimeUserDiscount: {
                          ...settings.firstTimeUserDiscount,
                          type: e.target.value as 'percentage' | 'flat',
                        },
                      })
                    }
                    sx={{ mt: 1 }}
                  >
                    <FormControlLabel
                      value="percentage"
                      control={<Radio />}
                      label="Percentage Discount"
                    />
                    <FormControlLabel value="flat" control={<Radio />} label="Fixed Amount Off" />
                  </RadioGroup>
                </FormControl>
              </Box>

              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    Percentage
                  </Typography>
                  <TextField
                    type="number"
                    value={settings.firstTimeUserDiscount.percentage}
                    onChange={(e) => {
                      setSettings({
                        ...settings,
                        firstTimeUserDiscount: {
                          ...settings.firstTimeUserDiscount,
                          percentage: Number(e.target.value),
                        },
                      });
                      clearError('firstTimeDiscountPercentage');
                    }}
                    inputProps={{ min: 0, max: 100 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">% off</InputAdornment>,
                    }}
                    error={!!errors.firstTimeDiscountPercentage}
                    helperText={errors.firstTimeDiscountPercentage || 'Used when type is "percentage"'}
                    fullWidth
                    disabled={settings.firstTimeUserDiscount.type !== 'percentage'}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" fontWeight="medium" gutterBottom>
                    Flat Amount
                  </Typography>
                  <TextField
                    type="number"
                    value={settings.firstTimeUserDiscount.flatAmount}
                    onChange={(e) => {
                      setSettings({
                        ...settings,
                        firstTimeUserDiscount: {
                          ...settings.firstTimeUserDiscount,
                          flatAmount: Number(e.target.value),
                        },
                      });
                      clearError('firstTimeDiscountFlatAmount');
                    }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                    }}
                    error={!!errors.firstTimeDiscountFlatAmount}
                    helperText={errors.firstTimeDiscountFlatAmount || 'Used when type is "flat"'}
                    fullWidth
                    disabled={settings.firstTimeUserDiscount.type !== 'flat'}
                  />
                </Grid>
              </Grid>

              <Box sx={{ mb: 3 }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    <Typography variant="body1" fontWeight="medium" color="text.primary">
                      Apply Discount To
                    </Typography>
                  </FormLabel>
                  <RadioGroup
                    value={settings.firstTimeUserDiscount.applyTo}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        firstTimeUserDiscount: {
                          ...settings.firstTimeUserDiscount,
                          applyTo: e.target.value as 'subtotal' | 'rentalCost',
                        },
                      })
                    }
                    sx={{ mt: 1 }}
                  >
                    <FormControlLabel
                      value="subtotal"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            Subtotal
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Rental cost + booking fee + driver fee
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="rentalCost"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            Rental Cost Only
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Discount applies to rental cost only
                          </Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              <Alert severity="success" icon={<InfoIcon />}>
                <Typography variant="body2">
                  <strong>Example:</strong> 10% off subtotal = User saves ₦5,250 on a ₦52,500 booking
                  (₦50,000 rental + ₦2,500 booking fee)
                </Typography>
              </Alert>
            </>
          )}
        </CardContent>
      </Card>

      {/* VAT Configuration - Regional */}
      <RegionalVATConfig
        vatConfigs={settings.vat}
        onChange={(configs) => setSettings({ ...settings, vat: configs })}
        errors={errors}
        clearError={clearError}
      />

      {/* Delivery Fee Configuration - Regional */}
      <RegionalDeliveryFeeConfig
        deliveryFeeConfigs={settings.deliveryFee}
        onChange={(configs) => setSettings({ ...settings, deliveryFee: configs })}
        errors={errors}
        clearError={clearError}
      />

      {/* Security Deposit Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Security Deposit
          </Typography>
          {/* Enable Security Deposit */}
          <Box sx={{ mb: 3 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={settings.requireSecurityDeposit}
                  onChange={(e) =>
                    setSettings({ ...settings, requireSecurityDeposit: e.target.checked })
                  }
                />
              }
              label={
                <Box>
                  <Typography variant="body1" fontWeight="medium">
                    Require Security Deposit for Rentals
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Collect refundable deposits to cover potential damages or violations
                  </Typography>
                </Box>
              }
            />
          </Box>

          {settings.requireSecurityDeposit && (
            <>
              <Divider sx={{ my: 3 }} />

              {/* Calculation Method */}
              <Box sx={{ mb: 3 }}>
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    <Typography variant="body1" fontWeight="medium" color="text.primary">
                      Deposit Calculation Method
                    </Typography>
                  </FormLabel>
                  <RadioGroup
                    value={settings.securityDepositType}
                    onChange={(e) =>
                      setSettings({
                        ...settings,
                        securityDepositType: e.target.value as 'percentage' | 'flat',
                      })
                    }
                    sx={{ mt: 1 }}
                  >
                    <FormControlLabel
                      value="percentage"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            Percentage of Rental Cost
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Deposit scales with rental price
                          </Typography>
                        </Box>
                      }
                    />
                    <FormControlLabel
                      value="flat"
                      control={<Radio />}
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            Fixed Amount
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Same deposit for all bookings
                          </Typography>
                        </Box>
                      }
                    />
                  </RadioGroup>
                </FormControl>
              </Box>

              {/* Percentage Input */}
              {settings.securityDepositType === 'percentage' && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" fontWeight="medium" gutterBottom>
                    Deposit Percentage
                  </Typography>
                  <TextField
                    type="number"
                    value={settings.securityDepositPercentage}
                    onChange={(e) => {
                      setSettings({ ...settings, securityDepositPercentage: Number(e.target.value) });
                      clearError('securityDepositPercentage');
                    }}
                    inputProps={{ min: 1, max: 100 }}
                    InputProps={{
                      endAdornment: <InputAdornment position="end">%</InputAdornment>,
                    }}
                    error={!!errors.securityDepositPercentage}
                    helperText={errors.securityDepositPercentage || 'Recommended: 15-25% of rental cost'}
                    fullWidth
                  />
                </Box>
              )}

              {/* Flat Amount Input */}
              {settings.securityDepositType === 'flat' && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="body1" fontWeight="medium" gutterBottom>
                    Fixed Deposit Amount
                  </Typography>
                  <TextField
                    type="number"
                    value={settings.securityDepositFlatAmount}
                    onChange={(e) => {
                      setSettings({ ...settings, securityDepositFlatAmount: Number(e.target.value) });
                      clearError('securityDepositFlatAmount');
                    }}
                    inputProps={{ min: 1000 }}
                    InputProps={{
                      startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                    }}
                    error={!!errors.securityDepositFlatAmount}
                    helperText={
                      errors.securityDepositFlatAmount ||
                      'Same amount for all bookings regardless of rental cost'
                    }
                    fullWidth
                  />
                </Box>
              )}

              <Divider sx={{ my: 3 }} />

              {/* Deposit Limits */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Deposit Limits
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" fontWeight="medium" gutterBottom>
                      Minimum Deposit
                    </Typography>
                    <TextField
                      type="number"
                      value={settings.minSecurityDeposit}
                      onChange={(e) => {
                        setSettings({ ...settings, minSecurityDeposit: Number(e.target.value) });
                        clearError('minSecurityDeposit');
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                      }}
                      error={!!errors.minSecurityDeposit}
                      helperText={errors.minSecurityDeposit || 'Lowest allowed deposit amount'}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" fontWeight="medium" gutterBottom>
                      Maximum Deposit
                    </Typography>
                    <TextField
                      type="number"
                      value={settings.maxSecurityDeposit}
                      onChange={(e) => {
                        setSettings({ ...settings, maxSecurityDeposit: Number(e.target.value) });
                        clearError('maxSecurityDeposit');
                      }}
                      InputProps={{
                        startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                      }}
                      error={!!errors.maxSecurityDeposit}
                      helperText={errors.maxSecurityDeposit || 'Highest allowed deposit amount'}
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Elite Member Discounts */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <StarIcon color="warning" />
                  Polar Elite Member Discounts
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Reduce security deposits for Polar Elite members to reward loyalty. Enter the percentage
                  reduction for each tier.
                </Alert>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Chip label="Silver" size="small" sx={{ bgcolor: '#C0C0C0' }} />
                      <Typography variant="body2" fontWeight="medium">
                        Elite Silver Discount
                      </Typography>
                    </Box>
                    <TextField
                      type="number"
                      value={settings.eliteTierDiscounts.silver}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          eliteTierDiscounts: {
                            ...settings.eliteTierDiscounts,
                            silver: Number(e.target.value),
                          },
                        })
                      }
                      inputProps={{ min: 0, max: 100 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">% off</InputAdornment>,
                      }}
                      helperText="Recommended: 25%"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Chip label="Gold" size="small" sx={{ bgcolor: '#FFD700' }} />
                      <Typography variant="body2" fontWeight="medium">
                        Elite Gold Discount
                      </Typography>
                    </Box>
                    <TextField
                      type="number"
                      value={settings.eliteTierDiscounts.gold}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          eliteTierDiscounts: {
                            ...settings.eliteTierDiscounts,
                            gold: Number(e.target.value),
                          },
                        })
                      }
                      inputProps={{ min: 0, max: 100 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">% off</InputAdornment>,
                      }}
                      helperText="Recommended: 50%"
                      fullWidth
                    />
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Chip label="Platinum" size="small" sx={{ bgcolor: '#E5E4E2' }} />
                      <Typography variant="body2" fontWeight="medium">
                        Elite Platinum Discount
                      </Typography>
                    </Box>
                    <TextField
                      type="number"
                      value={settings.eliteTierDiscounts.platinum}
                      onChange={(e) =>
                        setSettings({
                          ...settings,
                          eliteTierDiscounts: {
                            ...settings.eliteTierDiscounts,
                            platinum: Number(e.target.value),
                          },
                        })
                      }
                      inputProps={{ min: 0, max: 100 }}
                      InputProps={{
                        endAdornment: <InputAdornment position="end">% off</InputAdornment>,
                      }}
                      helperText="Recommended: 75%"
                      fullWidth
                    />
                  </Grid>
                </Grid>

                {/* Live Preview */}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mt: 3,
                    bgcolor: 'success.lighter',
                    border: '1px solid',
                    borderColor: 'success.light',
                  }}
                >
                  <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                    Example Calculation
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    For a ₦150,000 rental with {settings.securityDepositPercentage}% deposit:
                  </Typography>
                  <Box component="ul" sx={{ mt: 1, pl: 3, mb: 0 }}>
                    <li>
                      <Typography variant="body2">
                        <strong>Regular User:</strong> ₦
                        {calculateDeposit(150000, settings, null).toLocaleString()}
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        <strong>Elite Silver ({settings.eliteTierDiscounts.silver}% off):</strong> ₦
                        {calculateDeposit(150000, settings, 'silver').toLocaleString()}
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        <strong>Elite Gold ({settings.eliteTierDiscounts.gold}% off):</strong> ₦
                        {calculateDeposit(150000, settings, 'gold').toLocaleString()}
                      </Typography>
                    </li>
                    <li>
                      <Typography variant="body2">
                        <strong>Elite Platinum ({settings.eliteTierDiscounts.platinum}% off):</strong> ₦
                        {calculateDeposit(150000, settings, 'platinum').toLocaleString()}
                      </Typography>
                    </li>
                  </Box>
                </Paper>
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Automatic Deposit Release */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Automatic Deposit Release
                </Typography>
                <Typography variant="body2" fontWeight="medium" gutterBottom>
                  Auto-Release After Rental Ends
                </Typography>
                <TextField
                  type="number"
                  value={settings.depositReleaseHours}
                  onChange={(e) => {
                    setSettings({ ...settings, depositReleaseHours: Number(e.target.value) });
                    clearError('depositReleaseHours');
                  }}
                  inputProps={{ min: 0, max: 168 }}
                  InputProps={{
                    endAdornment: <InputAdornment position="end">hours</InputAdornment>,
                  }}
                  error={!!errors.depositReleaseHours}
                  helperText={
                    errors.depositReleaseHours ||
                    'Deposits are automatically released X hours after rental ends (if no incidents). Recommended: 48 hours. Maximum: 168 hours (1 week).'
                  }
                  fullWidth
                  sx={{ mb: 2 }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={settings.enableDepositNotifications}
                      onChange={(e) =>
                        setSettings({ ...settings, enableDepositNotifications: e.target.checked })
                      }
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        Send Deposit Release Notifications
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Notify renters when their deposit is released/refunded
                      </Typography>
                    </Box>
                  }
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              {/* Payment Method Info */}
              <Alert severity="warning" icon={<WarningIcon />}>
                <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
                  Payment Method Handling
                </Typography>
                <Box component="ul" sx={{ mt: 1, pl: 3, mb: 0 }}>
                  <li>
                    <Typography variant="body2">
                      <strong>Card Payments:</strong> Authorization hold placed on renter's card.
                      Automatically released after {settings.depositReleaseHours} hours.
                    </Typography>
                  </li>
                  <li>
                    <Typography variant="body2">
                      <strong>Bank Transfers:</strong> Full deposit charged upfront. Refunded to renter's
                      bank account after rental ends (1-3 business days).
                    </Typography>
                  </li>
                </Box>
              </Alert>
            </>
          )}
        </CardContent>
      </Card>

      {/* Action Buttons */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </Button>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleReset}
          disabled={saving}
          startIcon={<ResetIcon />}
        >
          Reset to Default
        </Button>
      </Box>
    </Box>
  );
};

export default PaymentSettingsPage;
